import { DependencyList, useEffect, useRef } from 'react'

export interface UseIntersectionSentinelOptions {
  rootMargin?: IntersectionObserverInit['rootMargin']
  threshold?: IntersectionObserverInit['threshold']
  deps?: DependencyList
}

export const useIntersectionSentinel = <R extends HTMLElement, S extends HTMLElement>(
  onIntersectionCallback: VoidFunction,
  options: UseIntersectionSentinelOptions = {},
) => {
  const { rootMargin = '200px', threshold = 0, deps = [] } = options

  const callbackRef = useRef(onIntersectionCallback)
  callbackRef.current = onIntersectionCallback

  const rootRef = useRef<R | null>(null)
  const sentinelRef = useRef<S | null>(null)

  useEffect(() => {
    const sentinel = sentinelRef.current

    if (!sentinel) return

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        if (!entry?.isIntersecting) return
        callbackRef.current()
      },
      { root: rootRef.current, rootMargin, threshold },
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [rootMargin, threshold, ...deps])

  return { rootRef, sentinelRef }
}
